import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Input } from "./input";
import { ChevronUp, ChevronDown, Calendar } from "lucide-react";

const MonthYearPicker = React.forwardRef(({ 
  value, 
  onChange, 
  className,
  id,
  name,
  ...props 
}, ref) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedYear, setSelectedYear] = React.useState(() => {
    if (value) {
      const [year] = value.split('-');
      return parseInt(year);
    }
    return new Date().getFullYear();
  });
  const [selectedMonth, setSelectedMonth] = React.useState(() => {
    if (value) {
      const [, month] = value.split('-');
      return parseInt(month);
    }
    return new Date().getMonth() + 1;
  });
  const [manualYear, setManualYear] = React.useState(selectedYear.toString());
  
  const months = [
    { value: 1, label: 'Jan' },
    { value: 2, label: 'Feb' },
    { value: 3, label: 'Mar' },
    { value: 4, label: 'Apr' },
    { value: 5, label: 'May' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dec' },
  ];

  const formatDisplay = () => {
    if (!value) return '';
    const [year, month] = value.split('-');
    const monthObj = months.find(m => m.value === parseInt(month));
    return `${monthObj?.label || ''} ${year}`;
  };

  const handleYearUp = () => {
    const newYear = selectedYear + 1;
    setSelectedYear(newYear);
    setManualYear(newYear.toString());
  };

  const handleYearDown = () => {
    const newYear = selectedYear - 1;
    setSelectedYear(newYear);
    setManualYear(newYear.toString());
  };

  const handleManualYearChange = (e) => {
    const val = e.target.value;
    setManualYear(val);
    const parsed = parseInt(val);
    if (!isNaN(parsed) && parsed >= 1900 && parsed <= 2100) {
      setSelectedYear(parsed);
    }
  };

  const handleManualYearBlur = () => {
    const parsed = parseInt(manualYear);
    if (isNaN(parsed) || parsed < 1900 || parsed > 2100) {
      setManualYear(selectedYear.toString());
    }
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    const formattedMonth = month.toString().padStart(2, '0');
    const newValue = `${selectedYear}-${formattedMonth}`;
    onChange?.({ target: { name, value: newValue } });
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange?.({ target: { name, value: '' } });
    setIsOpen(false);
  };

  const handleThisMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    setSelectedYear(year);
    setSelectedMonth(month);
    setManualYear(year.toString());
    const formattedMonth = month.toString().padStart(2, '0');
    onChange?.({ target: { name, value: `${year}-${formattedMonth}` } });
    setIsOpen(false);
  };

  // Update internal state when value prop changes
  React.useEffect(() => {
    if (value) {
      const [year, month] = value.split('-');
      setSelectedYear(parseInt(year));
      setSelectedMonth(parseInt(month));
      setManualYear(year);
    }
  }, [value]);

  return (
    <div className={cn("relative", className)} ref={ref}>
      <div 
        className="flex items-center border border-slate-200 rounded-sm h-11 px-3 cursor-pointer bg-white hover:border-slate-300 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Calendar className="h-4 w-4 text-slate-400 mr-2" />
        <span className={cn("flex-1", !value && "text-slate-400")}>
          {formatDisplay() || 'Select month & year'}
        </span>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 p-4 w-[280px]">
          {/* Year Selector with Arrows */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handleYearDown}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            
            <Input
              type="number"
              value={manualYear}
              onChange={handleManualYearChange}
              onBlur={handleManualYearBlur}
              className="w-20 h-8 text-center font-semibold text-[#0f2744]"
              min={1900}
              max={2100}
            />
            
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handleYearUp}
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          </div>

          {/* Month Grid */}
          <div className="grid grid-cols-4 gap-2">
            {months.map((month) => (
              <button
                key={month.value}
                type="button"
                className={cn(
                  "px-2 py-2 text-sm rounded-md transition-colors",
                  selectedMonth === month.value && selectedYear.toString() === manualYear
                    ? "bg-[#0f2744] text-white"
                    : "hover:bg-slate-100 text-slate-700"
                )}
                onClick={() => handleMonthSelect(month.value)}
              >
                {month.label}
              </button>
            ))}
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800"
              onClick={handleClear}
            >
              Clear
            </button>
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800"
              onClick={handleThisMonth}
            >
              This month
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
});

MonthYearPicker.displayName = "MonthYearPicker";

export { MonthYearPicker };
